/**
 * The Petty Cash ledger/reconciliation view (docs/phases/
 * petty-cash-consolidation.md, Part 2 "Reconciliation surfacing" and Part 3):
 * every entry in ledger order, each carrying the `balance_discrepancy` flag
 * the backend already computed - this screen adds nothing beyond filtering
 * and highlighting it.
 *
 * Gated by `require_reconciliation_viewer` like Cash Drop's and Safe Count's
 * (`GET /petty-cash/reconciliation`), same D-7 posture: the nav link is
 * always shown, and the screen renders exactly what the endpoint answers - a
 * `submitter`'s 403 as content, a viewer's 200 as the table. In practice the
 * only two people who ever touch the physical petty cash box (Massiel,
 * Kevin) are already `reconciliation_viewer`s in `access.yaml`, so this is
 * also where they read the current balance before filling in their next
 * entry's Petty Cash Balance field.
 *
 * Void (docs/phases/PHASE_cash-drop-entered-deposit.md, Part B): each live row
 * has a Void button; it opens an in-page reason prompt (no `window.confirm`),
 * posts to `/petty-cash/submissions/{id}/void`, and refetches the ledger so the
 * recomputed balances show. Voided rows stay, tagged `voided` with the amount
 * struck through. A server refusal (403, 409) is shown verbatim in the prompt.
 */
import { useEffect, useState } from "react";

import {
  ENTRY_TYPE_LABELS,
  fetchPettyCashReconciliation,
  formatDetail,
  voidPettyCash,
  type Observed,
  type PettyCashReconciliationOut,
  type PettyCashRecord,
} from "../api/public";
import type { TokenGetter } from "../auth/session";
import { errorMessage } from "../components/AttachmentSlots";
import { ReconciliationNav } from "../components/ReconciliationNav";
import { SessionGate } from "../components/SessionGate";

export function PettyCashReconciliation() {
  return (
    <>
      <ReconciliationNav />
      <SessionGate>{(getToken) => <PettyCashReconciliationView getToken={getToken} />}</SessionGate>
    </>
  );
}

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; result: Observed<PettyCashReconciliationOut>; refetching: boolean };

function PettyCashReconciliationView({ getToken }: { getToken: TokenGetter }) {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [state, setState] = useState<FetchState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [voiding, setVoiding] = useState<PettyCashRecord | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  function startVoid(row: PettyCashRecord) {
    setVoiding(row);
    setVoidReason("");
    setVoidError(null);
  }

  async function confirmVoid() {
    if (!voiding || voidBusy) return;
    if (voidReason.trim().length < 3) {
      setVoidError("Enter a reason of at least 3 characters.");
      return;
    }
    setVoidBusy(true);
    setVoidError(null);
    try {
      const token = await getToken();
      await voidPettyCash(voiding.id, voidReason.trim(), token);
      setVoiding(null);
      setReloadKey((k) => k + 1);
    } catch (error) {
      setVoidError(errorMessage(error));
    } finally {
      setVoidBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setState((prev) => (prev.kind === "loaded" ? { ...prev, refetching: true } : { kind: "loading" }));
    (async () => {
      const token = await getToken();
      const result = await fetchPettyCashReconciliation({ dateFrom, dateTo }, token);
      if (!cancelled) setState({ kind: "loaded", result, refetching: false });
    })();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, getToken, reloadKey]);

  if (state.kind === "loading") {
    return <p className="muted">Loading petty cash ledger…</p>;
  }

  const { result, refetching } = state;
  const detail =
    result.body && typeof result.body === "object" && "detail" in result.body
      ? formatDetail(result.body.detail)
      : "";

  if (result.status === 403) {
    return (
      <section className="card" data-testid="petty-cash-reconciliation-refused">
        <h2>Petty Cash</h2>
        <p>{detail || "You do not have access to the reconciliation view."}</p>
      </section>
    );
  }

  if (!result.ok || !result.body || !("rows" in result.body)) {
    return (
      <section className="card error" data-testid="petty-cash-reconciliation-error">
        <h2>Petty Cash</h2>
        <p>{detail || `Request failed (${result.status}).`}</p>
      </section>
    );
  }

  const data: PettyCashReconciliationOut = result.body;

  return (
    <>
      <section className="card">
        <h2>Petty Cash</h2>
        <div className="filters">
          <label className="field">
            From
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="field">
            To
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>
        {refetching && <p className="muted">Refreshing…</p>}
      </section>

      {voiding && (
        <section className="card" data-testid="void-prompt">
          <h2>Void entry</h2>
          <p>
            Void {voiding.purpose} ({voiding.transaction_date}, {voiding.signed_amount})? It stays in
            the history but stops counting toward the balance.
          </p>
          {voidError && (
            <p className="field-error" role="alert" data-testid="void-error">
              {voidError}
            </p>
          )}
          <label className="field">
            Reason
            <input
              type="text"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              disabled={voidBusy}
            />
          </label>
          <button type="button" onClick={() => void confirmVoid()} disabled={voidBusy}>
            {voidBusy ? "Voiding�" : "Confirm void"}
          </button>{" "}
          <button type="button" onClick={() => setVoiding(null)} disabled={voidBusy}>
            Cancel
          </button>
        </section>
      )}

      <section className="card" data-testid="petty-cash-reconciliation-table">
        {data.rows.length === 0 ? (
          <p className="muted">No petty cash entries in this range.</p>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Purpose</th>
                  <th>Type</th>
                  <th>Total</th>
                  <th>Reported balance</th>
                  <th>Expected balance</th>
                  <th>Submitted by</th>
                  <th>Photos</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.transaction_date}</td>
                    <td>{row.purpose}</td>
                    <td>{ENTRY_TYPE_LABELS[row.entry_type]}</td>
                    <td>
                      <span className={row.voided ? "voided-amount" : undefined}>{row.signed_amount}</span>
                      {row.voided && (
                        <span className="tag" title={`${row.voided_by ?? ""}: ${row.void_reason ?? ""}`}>
                          voided
                        </span>
                      )}
                    </td>
                    <td>{row.reported_balance}</td>
                    <td>
                      {row.expected_balance}
                      {row.balance_discrepancy && !row.voided && (
                        <span className="tag field-error" title={`Off by ${row.discrepancy_amount}`}>
                          discrepancy
                        </span>
                      )}
                    </td>
                    <td>{row.submitted_by}</td>
                    <td>{row.attachment_ids.length}</td>
                    <td>
                      {!row.voided && (
                        <button type="button" onClick={() => startVoid(row)}>
                          Void
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
